import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './TrustBar.css';

/**
 * TrustBar
 *
 * Truthful, launch-safe trust signals.  No unverifiable claims (customer counts,
 * uptime SLAs, testimonials).  Every badge represents an objectively verifiable
 * fact about the product:
 *
 *   • Stripe          — payments are processed by Stripe (verifiable at checkout).
 *   • SSL / HTTPS     — the site enforces HTTPS (verifiable in the address bar).
 *   • GDPR            — /privacy explains lawful basis + Swiss / EU compliance.
 *   • Made in Switzerland — the company is registered and operated in Switzerland.
 *
 * Variants:
 *   • `default` — spacious row for landing / footer pre-CTA blocks
 *   • `compact` — dense pill row for pricing / register sidebars
 */
export default function TrustBar({ variant = 'default', className = '' }) {
  const { t } = useLanguage();

  // NOTE: Copy lives in landing translation namespace `rl.trust.*` (added to all 4 locales).
  const items = [
    { key: 'stripe', label: t('rl.trust.stripe', 'Powered by Stripe'), icon: 'stripe' },
    { key: 'ssl', label: t('rl.trust.ssl', 'HTTPS / SSL'), icon: 'ssl' },
    { key: 'gdpr', label: t('rl.trust.gdpr', 'GDPR compliant'), icon: 'gdpr' },
    { key: 'swiss', label: t('rl.trust.swiss', 'Made in Switzerland'), icon: 'swiss' },
  ];

  return (
    <div className={`trust-bar trust-bar--${variant} ${className}`.trim()} data-testid="trust-bar" role="list">
      {items.map(it => (
        <div key={it.key} className="trust-bar__item" role="listitem">
          <TrustIcon name={it.icon} />
          <span className="trust-bar__label">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

function TrustIcon({ name }) {
  // Inline SVGs keep the bar zero-dependency and preload-friendly.
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true' };
  switch (name) {
    case 'stripe':
      // Stripe wordmark stand-in — CC card glyph (avoids trademark hazard)
      return (
        <svg {...common}>
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <path d="M2 10h20" />
          <path d="M6 15h2" />
        </svg>
      );
    case 'ssl':
      return (
        <svg {...common}>
          <rect x="4" y="10" width="16" height="10" rx="2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
      );
    case 'gdpr':
      return (
        <svg {...common}>
          <path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3Z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case 'swiss':
      return (
        <svg {...common} viewBox="0 0 24 24">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M12 8v8" />
          <path d="M8 12h8" />
        </svg>
      );
    default:
      return null;
  }
}
